// src/pages/configuracion/ConfiguracionPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../api/walletApi';

type ConfigItem = api.FinancialConfig;

const LABELS: Record<string, { label: string; desc: string; type: 'number' | 'boolean' | 'text' }> = {
  PORCENTAJE_COMISION_MVZ:              { label: 'Porcentaje de comisión (%)', desc: 'Feriagana cobra al MVZ por cada servicio contraentrega', type: 'number' },
  MAX_SERVICIOS_CON_COMISION_PENDIENTE: { label: 'Máx. comisiones pendientes', desc: 'Umbral antes de suspender la wallet del MVZ', type: 'number' },
  BLOQUEAR_MVZ_POR_DEUDA:              { label: 'Bloquear MVZ por deuda', desc: 'Suspende la wallet cuando supera el umbral de comisiones', type: 'boolean' },
  HORAS_CONFIRMACION_AUTOMATICA:       { label: 'Horas confirmación automática', desc: 'Tras este tiempo el sistema confirma el servicio sin acción del cliente', type: 'number' },
};

export default function ConfiguracionPage() {
  const [configs, setConfigs]   = useState<ConfigItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState<string | null>(null); // id del item guardando
  const [initLoad, setInitLoad] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getConfiguraciones();
      setConfigs(data);
      // Pre-cargar valores en el estado de edición
      const vals: Record<string, string> = {};
      data.forEach((c) => { vals[c.id] = c.valor; });
      setEditValues(vals);
    } catch {
      // tabla vacía o error
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const showMsg = (msg: string, isError = false) => {
    if (isError) { setErrorMsg(msg); setTimeout(() => setErrorMsg(''), 4000); }
    else         { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); }
  };

  const handleSave = async (cfg: ConfigItem) => {
    const newVal = (editValues[cfg.id] ?? '').trim();
    if (!newVal) return showMsg('El valor no puede estar vacío', true);
    setSaving(cfg.id);
    try {
      const updated = await api.actualizarConfiguracion(cfg.id, newVal);
      setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      showMsg(`✓ ${cfg.clave} actualizado correctamente`);
    } catch (err: any) {
      showMsg(err.response?.data?.mensaje || 'Error al guardar', true);
    } finally {
      setSaving(null);
    }
  };

  const handleInicializar = async () => {
    setInitLoad(true);
    try {
      await api.inicializarConfig();
      showMsg('Configuración por defecto inicializada');
      await loadConfigs();
    } catch (err: any) {
      showMsg(err.response?.data?.mensaje || 'Error al inicializar', true);
    } finally {
      setInitLoad(false);
    }
  };

  const hayDatos = configs.length > 0;

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          <i className="bi bi-gear-fill me-2" style={{ color: 'var(--fg-green)', fontSize: '1.2rem' }} />
          Configuración financiera
        </h1>
        <p className="page-subtitle">
          Parámetros del módulo Wallet MVZ · Solo administradores pueden modificarlos
        </p>
      </div>

      {/* Mensajes */}
      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-check-circle-fill" />{successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-exclamation-triangle-fill" />{errorMsg}
        </div>
      )}

      {/* Card principal */}
      <div className="fg-card mb-4">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i className="bi bi-sliders me-2" style={{ color: 'var(--fg-green)' }} />
            Parámetros financieros
          </div>

          {/* Botón según si hay datos o no */}
          {!loading && !hayDatos && (
            <button
              className="btn-fg-primary"
              onClick={handleInicializar}
              disabled={initLoad}
            >
              {initLoad
                ? <><span className="spinner-border spinner-border-sm" /> Inicializando...</>
                : <><i className="bi bi-database-check" /> Inicializar valores por defecto</>
              }
            </button>
          )}
        </div>

        <div style={{ padding: '20px 22px' }}>
          {loading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border" style={{ color: 'var(--fg-green)' }} />
            </div>
          ) : !hayDatos ? (
            <div className="empty-state">
              <i className="bi bi-database-x" style={{ fontSize: '2.5rem', color: 'var(--fg-muted)', display: 'block', marginBottom: 12 }} />
              <p style={{ color: 'var(--fg-muted)', marginBottom: 8 }}>
                No hay configuración financiera registrada todavía.
              </p>
              <p style={{ fontSize: '0.82rem', color: 'var(--fg-muted)' }}>
                Haz clic en <strong>"Inicializar valores por defecto"</strong> para crear los parámetros iniciales.
              </p>
            </div>
          ) : (
            <div className="row g-3">
              {configs.map((cfg) => {
                const meta = LABELS[cfg.clave] ?? { label: cfg.clave, desc: cfg.descripcion ?? '', type: 'text' };
                const isBoolean = meta.type === 'boolean';
                const isSaving  = saving === cfg.id;
                const curVal    = editValues[cfg.id] ?? cfg.valor;

                return (
                  <div className="col-12 col-md-6" key={cfg.id}>
                    <div style={{
                      background: '#f8fafb',
                      border: '1px solid rgba(0,107,76,0.1)',
                      borderRadius: 12,
                      padding: '16px 18px',
                    }}>
                      {/* Header del parámetro */}
                      <div className="d-flex align-items-start justify-content-between mb-2 gap-2">
                        <div>
                          <code style={{ fontSize: '0.72rem', color: 'var(--fg-green)', display: 'block', marginBottom: 2 }}>
                            {cfg.clave}
                          </code>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--fg-text)' }}>
                            {meta.label}
                          </div>
                        </div>
                        {/* Valor actual como badge */}
                        <span style={{
                          background: 'var(--fg-green)',
                          color: 'white',
                          borderRadius: 20,
                          padding: '3px 12px',
                          fontSize: '0.85rem',
                          fontWeight: 800,
                          flexShrink: 0,
                        }}>
                          {cfg.valor}{meta.type === 'number' && cfg.clave.includes('HORA') ? ' h' : ''}
                          {cfg.clave.includes('PORCENTAJE') ? '%' : ''}
                        </span>
                      </div>

                      <p style={{ fontSize: '0.78rem', color: 'var(--fg-muted)', marginBottom: 12 }}>
                        {meta.desc}
                      </p>

                      {/* Campo editable */}
                      <div className="d-flex gap-2 align-items-center">
                        {isBoolean ? (
                          <select
                            className="form-select form-select-sm"
                            style={{ borderRadius: 8, fontSize: '0.875rem', flex: 1 }}
                            value={curVal}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, [cfg.id]: e.target.value }))}
                          >
                            <option value="true">true — Activado</option>
                            <option value="false">false — Desactivado</option>
                          </select>
                        ) : (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ borderRadius: 8, fontSize: '0.875rem', flex: 1 }}
                            value={curVal}
                            min={0}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, [cfg.id]: e.target.value }))}
                          />
                        )}
                        <button
                          className="btn-fg-primary"
                          style={{ padding: '6px 14px', fontSize: '0.8rem', flexShrink: 0 }}
                          onClick={() => handleSave(cfg)}
                          disabled={isSaving || curVal === cfg.valor}
                        >
                          {isSaving
                            ? <span className="spinner-border spinner-border-sm" />
                            : <><i className="bi bi-check-lg" /> Guardar</>
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Guía del flujo de retiros */}
      <div className="fg-card">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i className="bi bi-book me-2" style={{ color: 'var(--fg-green)' }} />
            Flujo de retiros — Guía rápida
          </div>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <div className="row g-3 mb-4">
            {[
              { step: '1', icon: 'bi-phone',         title: 'MVZ solicita retiro',    desc: 'El MVZ desde la app solicita retirar su saldo disponible indicando cuenta bancaria.',      color: 'var(--fg-green)' },
              { step: '2', icon: 'bi-hourglass-split',title: 'Estado: PENDIENTE',     desc: 'Aparece en el panel con estado PENDIENTE. El saldo queda reservado.',                    color: 'var(--fg-orange)' },
              { step: '3', icon: 'bi-check-circle',   title: 'Admin aprueba',         desc: 'El admin revisa los datos bancarios y hace clic en "Aprobar". Pasa a APROBADO.',         color: 'var(--fg-green)' },
              { step: '4', icon: 'bi-bank',           title: 'Transferencia y cierre', desc: 'Se hace la transferencia bancaria real y se marca como "Procesado".',                    color: 'var(--fg-blue)' },
            ].map((s) => (
              <div className="col-12 col-sm-6 col-xl-3" key={s.step}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: s.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0, color: s.color }}>
                    <i className={`bi ${s.icon}`} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--fg-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>Paso {s.step}</div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>{s.title}</div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--fg-muted)', margin: 0, lineHeight: 1.5 }}>{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="alert alert-warning d-flex align-items-start gap-2 mb-0" style={{ borderRadius: 10, fontSize: '0.84rem' }}>
            <i className="bi bi-exclamation-triangle-fill mt-1" />
            <span>
              <strong>Servicios CONTRAENTREGA:</strong> El cliente paga efectivo al MVZ. La wallet acumula el ingreso y cobra la comisión configurada. El retiro de fondos <strong>siempre requiere aprobación del admin</strong>, sin importar el método de pago del servicio.
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
